import { toPascalCase } from '@/Utils/StringUtility';

function serializeNavigationTarget(to) {
    if (!to) {
        return '';
    }
    if (typeof to === 'string') {
        return to;
    }
    const params = to.params ? Object.values(to.params).join(',') : '';
    return `${to.name ?? ''}|${params}`;
}

// Deterministic so it stays stable across rebuilds - the v-for key must not change or
// Vue remounts every item and loses per-item state (e.g. expanded groups).
function buildNavigationItemId(navigationItemType, key, to) {
    return `${navigationItemType}:${key}:${serializeNavigationTarget(to)}`;
}

/**
 * Types of navigation links.
 * @readonly
 * @enum {string}
 */
export const navigationItemTypes = {
    /**
     * Internal links using Vue Router.
     */
    internal: 'internal',
    /**
     * External links, such as HTML links.
     */
    external: 'external',
    /**
     * Internal HTML links.
     */
    link: 'link',
    /**
     * Actions that are not links.
     */
    action: 'action',
};

/**
 * Creates a navigation item with the specified options.
 *
 * @param {Object} options - An object containing the following properties:
 * @param {string} options.label - The label or text for the navigation item.
 * @param {string|Object} options.to - The destination of the navigation item, which can be a Vue Router route name or an external URL, depending on the `navigationType`.
 * @param {boolean} [options.isEnabled=true] - Indicates whether the navigation item is enabled.
 * @param {string} [options.iconName] - The name of the icon associated with the navigation item (optional).
 * @param {Array} [options.subItems] - An array of sub-navigation items (optional).
 * @param {string} [options.navigationItemType='internal'] - The type of navigation link, which can be 'internal' (Vue Router link), 'external' (HTML link), 'link' (internal HTML link), or 'action' (non-link action).
 * @param {Function} [options.action] - An optional function to execute when the navigation item is activated (optional).
 * @param {string} [options.namespace='fa'] - The namespace for the icon, defaulting to 'fa' (Font Awesome).
 * @param {string} [options.groupType='default'] - The group type for the navigation item, defaulting to 'default'.
 *
 * @returns {Object} A navigation item object.
 * @throws {Error} If an unsupported navigationItemType is provided.
 */
export function createNavigationItem(options) {
    const {
        label,
        to,
        isEnabled = false,
        iconName = null,
        subItems = [],
        navigationItemType = navigationItemTypes.internal,
        action = () => { },
        namespace = 'fa',
        groupType = 'default',
        customComponent = null,
        isExactMatch = false,
    } = options;

    const icon = options.icon || { name: options.iconName || null, namespace };
    const key = options.key || toPascalCase(label);

    if (!Object.keys(navigationItemTypes).includes(navigationItemType)) {
        throw new Error('Unsupported link type');
    }

    return {
        key,
        navigationItemId: options.navigationItemId ?? buildNavigationItemId(navigationItemType, key, to),
        label,
        to,
        iconName,
        icon,
        isEnabled,
        navigationItemType,
        subItems,
        action,
        groupType,
        customComponent,
        isActive: options.isActive ?? false,
        isExactMatch,
    };
}

/**
 * Creates a collapsible navigation group (an item rendered as an accordion of `subItems`).
 * When `isEnabled` is omitted it defaults to "has at least one sub-item", so a group that
 * filters down to nothing is hidden automatically (and never renders a `to`-less link).
 *
 * @param {Object} options - `label`, `iconName`/`icon`, `namespace`, `subItems`, optional `isEnabled`, `groupType`.
 * @returns {Object} A navigation group item.
 */
export function createNavigationGroup({ label, iconName, icon, namespace, subItems = [], isEnabled, groupType }) {
    return createNavigationItem({
        label,
        iconName,
        icon,
        namespace,
        groupType,
        subItems,
        isEnabled: isEnabled ?? subItems.length > 0,
    });
}

/**
 * Builds the base route params (creator + project url names) shared by project-scoped
 * navigation links. Returns `null` for a segment whose context is absent.
 *
 * @param {Object} projectContext - The admin project context.
 * @returns {{ creator: (string|null), project: (string|null) }}
 */
export function projectRouteBaseParams(projectContext) {
    return {
        creator: projectContext?.creator?.urlName ?? null,
        project: projectContext?.project?.urlName ?? null,
    };
}

/**
 * Creates a list of navigation items and filters it based on the provided filter function.
 *
 * @param {Array} items - An array of navigation items to filter.
 * @param {Function} [filter] - An optional filter function that determines which items are included in the resulting list (optional).
 *
 * @returns {Array} A filtered list of navigation items.
 */
export function createNavigationItemList(items, filter = item => item.isEnabled) {
    return items.filter(item => filter(item) && isRenderableNavigationItem(item));
}

/**
 * Determines whether a navigation item can render a valid element. Action items
 * render a button; every other type needs either a destination (`to`) or at least
 * one sub-item. An enabled item without any of these would render a `<router-link>`
 * with a null `to` (which Vue Router rejects), so it is treated as non-renderable.
 *
 * @param {Object} item - The navigation item to test.
 * @returns {boolean} True when the item can be rendered.
 */
function isRenderableNavigationItem(item) {
    const isActionItem = item.navigationItemType === navigationItemTypes.action;
    const doExist = item.to !== null && item.to !== undefined;
    const haveSubitems = Array.isArray(item.subItems) && item.subItems.length > 0;
    const hasCustomComponent = !!item.customComponent;

    return isActionItem
        || doExist
        || haveSubitems
        || hasCustomComponent;
}

/**
* Recursively filters a navigation tree by a search term. Leaf items are kept when
* their own label matches; groups (items with sub-items) are matched only through
* their descendants — a group's own label is never matched. Groups that retain any
* matching descendant keep just the matching subset and are flagged with
* `isExpandedBySearch` so the UI auto-expands the whole branch down to the deepest match.
*
* @param {Array} items - Navigation items (each may have nested `subItems`).
* @param {string} term - Lower-cased search term.
* @returns {Array} A new, filtered navigation tree.
*/
export function filterNavigationItems(items, term) {
    return items.reduce((acc, item) => {
        const hasSubItems = Array.isArray(item.subItems) && item.subItems.length > 0;

        if (hasSubItems) {
            // Group: match only through descendants, never by the group's own label.
            const matchedSubItems = filterNavigationItems(item.subItems, term);
            if (matchedSubItems.length) {
                acc.push({ ...item, subItems: matchedSubItems, isExpandedBySearch: !!term });
            }
        }
        else if ((item.label || '').toLowerCase().includes(term)) {
            // Leaf: match on its own label.
            acc.push({ ...item });
        }

        return acc;
    }, []);
}

/**
 * Flattens a navigation tree into a `{ [routeName]: icon }` lookup, walking `subItems`
 * recursively. Only items with an object route (`to.name`) and an icon are included, so
 * a routed screen can resolve its own icon from the navigation (e.g. for its header).
 *
 * @param {Array} items - Navigation items (each may have nested `subItems`).
 * @returns {Object<string, { name: string, namespace: string }>}
 */
export function flattenNavigationIcons(items) {
    const iconsByRouteName = {};

    const walk = navItems => {
        (navItems || []).forEach(item => {
            const routeName = item.to?.name;
            if (routeName && item.icon?.name) {
                iconsByRouteName[routeName] = item.icon;
            }
            if (item.subItems?.length) {
                walk(item.subItems);
            }
        });
    };

    walk(items);

    return iconsByRouteName;
}

/**
 * Configures target and rel attributes for external navigation items.
 *
 * @param {Object} item - The navigation item to configure.
 * @returns {Object} The navigation item with updated target and rel attributes.
 */
export function configureExternalNavigationItems(item) {
    return {
        ...item,
        target: navigationItemTypes.external === item.navigationItemType ? '_blank' : null,
        rel: navigationItemTypes.external === item.navigationItemType ? 'noopener noreferrer' : null,
        isTypeExternal: navigationItemTypes.external === item.navigationItemType,
        tag: navigationItemTypes.action === item.navigationItemType ? 'button' : 'a',
    };
}